/**
 * The cloud witness (PLAN §8.4, D9: "the cloud: both site links report
 * last-seen per node"; wave 4c) and which witness is in force on a node.
 *
 * THE BEAT. A node of a main/backup pair sends a tiny `witness.beat` frame
 * over its site link every `CLOUD_WITNESS_BEAT_MS`; the cloud stamps its own
 * time for that node on its pair's witness record and answers `witness.view`
 * (naming the beat in `inReplyTo`): the pair's other node, with how long
 * ago the CLOUD last heard it and whether that is within
 * `CLOUD_WITNESS_UNSEEN_MS`. The answer is the node's proof of contact: its
 * lease counts from when the acknowledged beat was SENT (the cloud stamps
 * arrival, so the node's view of the contact is never later than the
 * cloud's). `witness.ask` asks for a fresh view (the failover decision's "can
 * you see node X?"), and a node that comes back after being unseen is pushed
 * to the site's other nodes at once.
 *
 * THE LEASE. A beat a second through API Gateway and a Lambda is coarse and
 * jittery (a cold start is ~1 s), so the cloud cannot renew a lease on the
 * LAN witness's 600 ms: an active whose only witness is the cloud fences
 * itself after `CLOUD_WITNESS_LEASE_MS` without an acknowledged beat, and the
 * cloud reports a node lost only after `CLOUD_WITNESS_UNSEEN_MS` — strictly
 * longer, so an active that lost the cloud is muted before the cloud can
 * confirm it gone (the same inequality `timingsProblem` holds for a LAN
 * witness: `selfFenceMs < witnessUnseenMs`). Two lost beats in a row, or one
 * beat whose answer takes over 2 s, fence a healthy active: the price of the
 * coarse grain.
 *
 * WHICH WITNESS IS IN FORCE (docs/redundancy.md, "Which witness decides"):
 * the cloud counts only on a site WITHOUT a LAN witness. With exactly one LAN
 * witness W and the cloud C, any split-brain-safe rule either ignores C or
 * requires both (confirming quorum + lease quorum > 2), and requiring both
 * would disable automatic failover during an internet outage — exactly when a
 * studio relies on it — and stretch every failover to the cloud's window.
 *
 * COST (per site of two nodes, dev region prices, 30 days): 2 × 86 400 × 30 ≈
 * 5.2 M beats and as many answers ≈ 10.4 M API Gateway WebSocket messages
 * (~$10.4 at $1.00/M), 5.2 M Lambda invocations of the ws function (~$1.0 of
 * requests + ~$0.3 of 256 MB arm64 duration) and 5.2 M single-item DynamoDB
 * updates (~$6.5 on demand) plus the connection lookups (~$0.7) — about
 * $19 a site a month, and only on sites whose pair has no LAN witness (the
 * beat is not sent otherwise).
 */
import { openEnum } from './openEnum.js';
import { z } from 'zod';

/** A node's beat to the cloud witness. */
export const CLOUD_WITNESS_BEAT_MS = 1_000;
/** An active whose only witness is the cloud mutes after this long without an acknowledged beat. */
export const CLOUD_WITNESS_LEASE_MS = 3_000;
/** The cloud reports a node lost once it has not heard a beat for this long (> the lease). */
export const CLOUD_WITNESS_UNSEEN_MS = 3_500;

/** Which witness confirms failovers and renews the lease on this node: its LAN witness, the cloud, or none. */
/** Open: a node reports it (its health, the pair report, the status frame); a reader of another release receives it. */
export const witnessModeWireSchema = openEnum(['lan', 'cloud', 'none']);

/**
 * A witness's kind in a node's report: its LAN witness or the cloud. Open, as the mode.
 */
export const witnessKindWireSchema = openEnum(['lan', 'cloud']);
export type WitnessKindWire = z.infer<typeof witnessKindWireSchema>;

/**
 * A node's place in its site's pair (D21), as a report names it: `main` or `backup`. Open — a reader of another
 * release receives it. (The role a node is TOLD it is, `siteNodeRoleWireSchema`, stays closed.)
 */
export const pairNodeRoleWireSchema = openEnum(['main', 'backup']);
export type PairNodeRoleWire = z.infer<typeof pairNodeRoleWireSchema>;

/**
 * A production's role on one node of the pair: `active` drives the devices, `standby` follows muted (D9). Closed:
 * the pair is two roles by construction — a third is a new protocol (a new IPC version and peer-link capability),
 * not a value an existing reader could act on.
 */
export const pairRoleWireSchema = z.enum(['active', 'standby']);
export type PairRoleWire = z.infer<typeof pairRoleWireSchema>;
export type WitnessModeWire = z.infer<typeof witnessModeWireSchema>;

export const witnessInForceWireSchema = z
  .object({
    mode: witnessModeWireSchema,
    /** Expected detect-and-promote time after the active dies (about 1 s with a LAN witness, 3.5 s cloud-only), or null: no automatic failover. */
    expectedFailoverMs: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type WitnessInForceWire = z.infer<typeof witnessInForceWireSchema>;

/** For people (`acp status`, the connectivity panel): `lan (~1.0 s)`, `cloud (~3.5 s)`, `none (no automatic failover)`. */
export function describeWitness(witness: WitnessInForceWire): string {
  if (witness.expectedFailoverMs === null) return `${witness.mode} (no automatic failover)`;
  return `${witness.mode} (~${(witness.expectedFailoverMs / 1000).toFixed(1)} s)`;
}
